import { amplitudeTrack, setAnalyticsUser } from '@cw-elements/analytics-provider';
import { analyticsReady } from '../../../analytics';

/**
 * Client-side port of the React accounts `useTrackAuth` hook
 * (web-react-cropwise-accounts/src/app/common/hooks/use-track-auth.ts), so the
 * server-rendered pages emit the named OAuth events identically to the SPA —
 * riding the same browser SDK context (device, OS, session, geo) from analytics.js.
 * Property logic mirrors the SPA's getProviderType / getAccessMethod / getLocale /
 * getStaffCampaignLink / getCampaignId / getFromSession.
 */
const COMPONENT = 'Accounts';
const CAMPAIGN_ID_KEY = 'campaignId';

// ?idp -> Amplitude provider "type" (matches the SPA's ProviderType enum).
const PROVIDER_TYPES = { google: 'Google', microsoft: 'Microsoft', apple: 'Apple' };

const searchParams = () => new URLSearchParams(location.search);
const fromSession = (key) => sessionStorage.getItem(key) || '';
const hasSignal = (param, sessionKey) => Boolean(
  searchParams().get(param) || fromSession(sessionKey),
);

export const getProviderType = (params = searchParams()) => {
  const idp = params.get('idp') || params.get('identity_provider');
  return (idp && PROVIDER_TYPES[idp.toLowerCase()]) || 'Local';
};

export const identifySignupUser = async (
  userId,
  redirectUrl,
  redirect = (url) => location.replace(url),
) => {
  await analyticsReady;

  try {
    setAnalyticsUser(userId);
  } catch (error) {
    // Analytics must not strand a successfully created account on this handoff page.
    // eslint-disable-next-line no-console
    console.error('[Analytics] Signup user identification failed:', error);
  }

  redirect(redirectUrl);
};

const SIGNUP_COMPLETION_FALLBACK_TIMEOUT_MS = 2000;

const showSignupFallbackLink = (signupCompletion) => {
  signupCompletion.querySelector('#signup-completion-fallback-link')
    ?.classList
    .add('devise-form-wrapper__signup-completion-fallback--visible');
};

// The sign-in page renders these from the server session: the deep-action flow
// no longer carries the campaign on the URL, so the server surfaces it as meta.
const metaContent = (name) => (
  document.querySelector(`meta[name="${CSS.escape(name)}"]`)?.content ?? null
);

// Campaign id/name come from the server-rendered meta first (deep-action flow),
// falling back to the sessionStorage the sign-up campaign path still populates.
const getCampaignId = () => metaContent('cwp-campaign-id')
  || sessionStorage.getItem(CAMPAIGN_ID_KEY)
  || null;
const getLinkName = () => metaContent('cwp-campaign-name');

// First match wins, mirroring the SPA's getAccessMethod switch. The campaign
// meta (server session) counts as a Campaign signal alongside the URL params.
const getAccessMethod = () => {
  if (hasSignal('inviteId', 'inviteId')) return 'Invite';
  if (getCampaignId() || hasSignal('source_campaign', 'source_campaign_signup')) return 'Campaign';
  return hasSignal('source_distribution_data', 'persistent_source_distribution_data')
    ? 'Distribution license'
    : 'Self-service';
};

// The SPA reads the locale from the cropwise-language cookie; the layout already
// renders it on <html lang>, so we read it back from there.
const getLocale = () => document.documentElement.lang || 'en';

// <env>.accounts.cropwise.com -> staff.<env>.cropwise.com (prod: staff.cropwise.com)
export const buildStaffCampaignLink = (campaignId, hostname) => {
  if (!campaignId) return null;

  const env = hostname?.match(/^(\w+)\.accounts\.cropwise\.com$/)?.[1];
  const staffSubdomain = env ? `staff.${env}.cropwise.com` : 'staff.cropwise.com';
  return `https://${staffSubdomain}/app/campaigns/${campaignId}`;
};

const getStaffCampaignLink = () => buildStaffCampaignLink(getCampaignId(), location.hostname);

const commonProperties = () => ({
  component: COMPONENT,
  type: getProviderType(),
  access: getAccessMethod(),
  region: getLocale(),
});

// client_id / redirect_uri arrive on the entry URL; persist them so they
// survive the multi-step flow (mirrors the SPA storing them in sessionStorage).
export const persistedParam = (key) => {
  const fromUrl = searchParams().get(key);
  if (fromUrl) sessionStorage.setItem(key, fromUrl);
  return fromSession(key);
};

export const trackOAuth = (eventName) => amplitudeTrack(eventName, {
  ...commonProperties(),
  link: getStaffCampaignLink(),
  'link name': getLinkName(),
  'campaign ID': getCampaignId(),
  'client ID': persistedParam('client_id'),
  'redirect URI': persistedParam('redirect_uri'),
});

export const trackSignDocs = (legalDoc) => amplitudeTrack(
  'user accept legal docs',
  { ...commonProperties(), 'legal document': legalDoc },
);

// Fire-and-forget on submit and let the form navigate normally: analytics.js
// inits Amplitude with beacon transport and flushes on page hide, so the queued
// event survives the navigation without us holding up (and breaking) the submit.
// A sign-up submit that only advances to the next in-page step (see
// signup_step_transition.js) is not a real submission — defaultPrevented
// guards against tracking "user sign-up" before the account actually exists.
const onSubmit = (form, eventName) => {
  form?.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    trackOAuth(eventName);
  });
};

/**
 * Wires the named OAuth/legal-docs events to the equivalent server-rendered
 * moments, mirroring the SPA's call sites. On a server-rendered form the submit
 * navigates away, so events fire on submit (attempt), not on confirmed success
 * as the SPA's promise callback does.
 */
export const initTrackAuth = () => {
  const signupCompletion = document.getElementById('signup-completion');
  if (signupCompletion) {
    const fallbackTimer = setTimeout(
      () => showSignupFallbackLink(signupCompletion),
      SIGNUP_COMPLETION_FALLBACK_TIMEOUT_MS,
    );

    const clearFallbackTimer = () => clearTimeout(fallbackTimer);
    window.addEventListener('pagehide', clearFallbackTimer, { once: true });

    const completeSignup = async () => {
      try {
        await identifySignupUser(
          signupCompletion.dataset.userId,
          signupCompletion.dataset.redirectUrl,
        );
      } catch {
        showSignupFallbackLink(signupCompletion);
      }
    };

    void completeSignup();
    return;
  }

  // External-IdP interstitial: the server rendered this page with ?idp=<provider>
  // and the SSO URL, so "user sign-in" carries the real provider before we hand
  // off. Fire once, then redirect; the beacon flush on pagehide survives it.
  const ssoRedirectUrl = document.querySelector('meta[name="cwp-sso-redirect"]')?.content;
  if (ssoRedirectUrl) {
    // Never let a tracking error strand the user on the interstitial: the SSO
    // hand-off must happen even if amplitudeTrack throws. The beacon transport
    // flushes the queued event on the pagehide that location.replace triggers.
    try {
      trackOAuth('user sign-in');
    } catch (error) {
      // Swallow — analytics must not block the redirect, but log so we can
      // detect when the interstitial hand-off fails to emit "user sign-in".
      // eslint-disable-next-line no-console
      console.error('[Analytics] OAuth sign-in tracking failed:', error);
    }
    location.replace(ssoRedirectUrl);
    return;
  }

  // Local sign-in: the credentials form carries #password-field but so do the
  // sign-up steps (which reuse the same field id), so exclude anything inside
  // the sign-up wrapper. External IdPs fire on the interstitial above instead
  // (no password form to hook).
  const signInForm = document.getElementById('password-field')?.closest('form');
  const isSignUpStep = signInForm?.closest('.devise-form-signup');
  if (signInForm && !isSignUpStep) onSubmit(signInForm, 'user sign-in');

  // Start sign-up: the email-entry form (the sign-up wrapper without the
  // register class, which is the later profile step).
  const startSignUp = document.querySelector('.devise-form-signup:not(.devise-form-register) form');
  onSubmit(startSignUp, 'user start signup');

  // Sign-up: the profile/registration submit.
  onSubmit(document.getElementById('registration-form'), 'user sign-up');

  // Legal docs: one event per document as it is checked. The label may wrap the
  // checkbox (bare <label>) or be a sibling linked by `for`, depending on the
  // page; resolve either, and skip tracking when the text can't be resolved.
  for (const checkbox of document.querySelectorAll('input[name="legal_doc_ids[]"]')) {
    checkbox.addEventListener('change', () => {
      if (!checkbox.checked) return;

      const label = checkbox.closest('label')
        || document.querySelector(`label[for="${CSS.escape(checkbox.id)}"]`);
      const text = label?.textContent.trim();
      if (text) trackSignDocs(text);
    });
  }
};
